<template>
  <div class="home-container px-6 py-6">
    <v-overlay v-model="drupalUnavailable" persistent class="align-center justify-center">
      <v-card max-width="520" class="text-center pa-8 rounded-xl" elevation="12">
        <v-icon size="72" color="warning" class="mb-4">mdi-server-network-off</v-icon>
        <v-card-title class="text-h5 font-weight-bold mb-2 justify-center">
          Sistema Temporariamente Indisponível
        </v-card-title>
        <v-card-text class="text-body-1 text-grey-darken-2">
          O sistema está em manutenção no momento.<br />
          Por favor, tente novamente mais tarde.<br />
          Em caso de dúvida, entre em contato com suporte<br />
          técnico através do email
          <a href="mailto:ticfe@unicamp.br">ticfe@unicamp.br</a>.
        </v-card-text>
      </v-card>
    </v-overlay>

    <h1 class="text-3xl md:text-4xl font-bold text-black mb-4 border-b-4 border-secondary pb-2">
      Processos Seletivos da Pós-graduação
    </h1>
    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-black mb-4">
        Inscrições Abertas / Em Andamento
      </h2>

      <v-row dense>
        <v-col v-for="process in activeProcesses" :key="process.id" :cols="12"
          :md="activeProcesses.length > 1 ? 6 : 12">
          <v-card class="mx-auto rounded-2xl shadow-lg hover:shadow-xl transition-shadow" elevation="8" color="#BBDEFB">
            <v-card-title class="d-flex flex-column bg-primary text-white rounded-t-2xl px-6 py-5">
              <div class="title-row">
                <v-icon size="28" class="mr-2">mdi-school</v-icon>
                <div class="title-text">
                  <span class="process-title">{{ process.base }}</span>
                  <span v-if="process.period" class="process-period">{{ process.period }}</span>
                </div>
              </div>

              <span class="text-subtitle-1 font-italic text-white text-opacity-80 mt-1 meta-line">
                Tipo: {{ translateType(process.type) }} | Status: {{ translateStatus(process.status) }}
              </span>

              <span class="text-white text-opacity-90 mt-2 phase-line">
                <template v-if="!hasDetail(process.id)">
                  Fase atual: <strong>carregando...</strong>
                </template>

                <template v-else>
                  <div v-if="getCurrentPhase(process.id)" class="text-wrap" style="white-space: normal; word-break: break-word;">
                    Fase atual:
                    <strong>{{ getPhaseLabel(getCurrentPhase(process.id)) }}</strong>
                    <span> — </span>
                    <strong>{{ formatDateTime(getCurrentPhase(process.id).start) }}</strong>
                    <span> a </span>
                    <strong>{{ formatDateTime(getCurrentPhase(process.id).end) }}</strong>
                  </div>
                  <template v-else>
                    Fase atual: <strong>—</strong>
                  </template>
                </template>
              </span>
            </v-card-title>

            <v-card-actions class="px-6 py-4 bg-grey-lighten-4 rounded-b-2xl card-actions">
              <div class="action-area">
                <v-btn v-if="isAuthenticated && userHasApplication(process.id)" variant="flat" color="primary"
                  class="text-white font-weight-medium px-6" rounded @click="handleEnroll(process, { summary: true })">
                  Comprovante
                </v-btn>

                <a v-if="shouldShowApprovedInActive(process.id)" href="#"
                  class="text-primary font-medium hover:underline"
                  @click.prevent="goToApproved(process.id, getApprovedPhaseId(process.id))">
                  Lista de aprovações
                </a>

                <v-btn v-else-if="shouldShowEnroll(process.id) && canRenderEnrollButton" variant="flat" color="primary"
                  class="text-white font-weight-medium px-6" rounded @click="handleEnroll(process, { summary: false })">
                  {{ getEnrollButtonLabel(process.id) }}
                </v-btn>

                <v-btn v-else-if="shouldShowEnroll(process.id) && !canRenderEnrollButton" variant="flat" color="grey"
                  class="text-white font-weight-medium px-6" rounded disabled>
                  Carregando...
                </v-btn>

                <v-chip v-else class="status-pill" color="grey-darken-2" variant="tonal" label>
                  {{ enrollStatusLabel(process.id) }}
                </v-chip>
              </div>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </section>

    <section>
      <h2 class="text-xl font-semibold text-black mb-2">Processos Finalizados</h2>

      <div class="mb-6 flex items-center flex-wrap gap-3">
        <v-select v-model="selectedYear" :items="availableYears" label="Selecione o ano" variant="outlined"
          density="comfortable" rounded="xl" class="max-w-xs" hide-details />
      </div>

      <v-row dense>
        <v-col v-for="process in filteredFinishedProcesses" :key="process.id" cols="12" md="6">
          <v-card class="mx-auto rounded-2xl shadow-md transition-shadow" elevation="4" color="#ECEFF1">
            <v-card-title class="d-flex flex-column px-6 py-4">
              <div class="title-row">
                <v-icon size="24" class="mr-2">mdi-school</v-icon>
                <div class="title-text">
                  <span class="process-title text-h6 font-weight-bold text-grey-darken-3">{{ process.base }}</span>
                  <span v-if="process.period" class="process-period text-subtitle-2 italic text-grey-darken-3">
                    {{ process.period }}
                  </span>
                </div>
              </div>

              <div class="text-caption text-grey-darken-2 mt-2">
                Tipo: {{ translateType(process.type) }} | Status: {{ translateStatus(process.status) }}
              </div>

              <div class="mt-2">
                <a href="#" class="text-primary font-medium hover:underline" @click.prevent="goToApproved(process.id)">
                  Lista de aprovações
                </a>
              </div>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>
    </section>
  </div>
</template>

<script>
import axios from 'axios';
import keycloak from '../plugins/keycloak';

export default {
  data() {
    return {
      items: [],
      selectedYear: null,
      activeDetailsById: {},
      userId: null,
      applicationByProcessId: {},
      applicationsLoaded: false,
      drupalUnavailable: false,
      userApplications: [],
    };
  },

  computed: {
    user() {
      return this.$store.getters.getUserData;
    },

    isAuthenticated() {
      return this.$store.state.isAuthenticated;
    },

    canRenderEnrollButton() {
      return !this.isAuthenticated || this.applicationsLoaded;
    },

    userInitials() {
      if (!this.user) return '';
      const first = this.user.given_name ? this.user.given_name[0] : '';
      const last = this.user.family_name ? this.user.family_name[0] : '';
      return first + last;
    },

    processedItems() {
      return (this.items || []).map((item) => {
        const name = item && item.name ? String(item.name) : '';
        const { base, period } = this.splitProcess(name);
        const year = this.extractYear(name);
        return { ...item, base, period, year };
      });
    },

    activeProcesses() {
      return this.processedItems.filter(
        (p) => p.status === 'started' || p.status === 'in_progress'
      );
    },

    finishedProcesses() {
      return this.processedItems.filter((p) => p.status === 'finished');
    },

    availableYears() {
      const years = this.finishedProcesses.map((p) => p.year).filter((y) => !!y);
      return [...new Set(years)].sort((a, b) => b - a);
    },

    filteredFinishedProcesses() {
      if (!this.selectedYear) return this.finishedProcesses;
      return this.finishedProcesses.filter((p) => p.year === this.selectedYear);
    },
  },

  async created() {
    const promises = [this.fetchProcesses()];
    if (this.isAuthenticated) {
      promises.push(this.loadUserContext());
    } else {
      this.applicationsLoaded = true;
    }

    await Promise.all(promises);
  },

  watch: {
    finishedProcesses(newVal) {
      if (newVal.length > 0 && !this.selectedYear) {
        this.selectedYear = this.availableYears[0] || null;
      }
    },
  },

  methods: {
    async loadUserContext() {
      try {
        await this.loadUserId();
        await this.loadUserApplications();
      } catch (error) {
        console.error('Erro ao carregar contexto do usuário:', error);
      } finally {
        this.applicationsLoaded = true;
      }
    },

    async loadUserId() {
      const uuid = keycloak?.tokenParsed?.sub;
      if (!uuid) {
        this.userId = null;
        return;
      }

      try {
        const res = await axios.get(`${this.$PROFPATH}/profiles`, {
          params: {
            filters: {
              kc_uuid: uuid,
            },
          },
        });
        this.userId = res.data?.data?.[0]?.id || null;
      } catch (error) {
        console.error('Erro ao buscar userId:', error);
        this.userId = null;
      }
    },

    async loadUserApplications() {
      if (!this.userId) {
        return;
      }
      this.userApplications = [];
      try {
        this.applicationByProcessId = {};

        // Carrega inscrições regulares
        const regularData = await axios.get(`${this.$BACKPATH}/regular-applications`, {
          params: {
            'filters[user_id]': this.userId,
          },
          headers: { Authorization: `Bearer ${keycloak.token}` },
        });

        const regularApps = regularData?.data?.data || [];
        regularApps.forEach((app) => {
          if (app?.process_id) {
            this.applicationByProcessId[String(app.process_id)] = app;
            this.userApplications.push(app);
          }
        });

        // Carrega inscrições especiais da API
        try {
          const specialData = await axios.get(`${this.$BACKPATH}/special-applications`, {
            params: {
              'filters[user_id]': this.userId,
            },
            headers: { Authorization: `Bearer ${keycloak.token}` },
          });

          const specialApps = specialData?.data?.data || [];
          specialApps.forEach((app) => {
            if (app?.process_id) {
              this.applicationByProcessId[String(app.process_id)] = app;
            }
          });
        } catch (specialError) {
          console.warn('Erro ao buscar special-applications com filtro:', specialError.message);
          
          // Tenta sem filtro
          try {
            const allSpecialData = await axios.get(`${this.$BACKPATH}/special-applications`, {
              headers: { Authorization: `Bearer ${keycloak.token}` },
            });

            const allSpecialApps = allSpecialData?.data?.data || [];
            
            // Filtra apenas as do usuário atual
            const userSpecialApps = allSpecialApps.filter(app => String(app?.user_id) === String(this.userId));
            
            userSpecialApps.forEach((app) => {
              if (app?.process_id) {
                this.applicationByProcessId[String(app.process_id)] = app;
              }
            });
          } catch (fallbackError) {
            console.warn('Erro ao buscar special-applications sem filtro:', fallbackError.message);
            
            // Fallback: Verificar localStorage para inscrições especiais
            //this.loadSpecialApplicationsFromLocalStorage();
          }
        }

        // Verificar localStorage para inscrições regulares também como fallback
        //this.loadApplicationsFromLocalStorage();

      } catch (error) {
        console.error('Erro ao buscar inscrições do usuário:', error);
        this.applicationByProcessId = {};
      }
    },
/*
    loadApplicationsFromLocalStorage() {
      // Procura por todas as chaves de aplicações regular no localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith(`regular-application-${this.userId}-`)) {
          const processId = key.replace(`regular-application-${this.userId}-`, '');
          if (!this.applicationByProcessId[processId]) {
            const cached = JSON.parse(localStorage.getItem(key));
            this.applicationByProcessId[processId] = cached;
          }
        }
      }
    },

    loadSpecialApplicationsFromLocalStorage() {
      // Procura por todas as chaves de aplicações especiais no localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith(`special-application-${this.userId}-`)) {
          const processId = key.replace(`special-application-${this.userId}-`, '');
          if (!this.applicationByProcessId[processId]) {
            const cached = JSON.parse(localStorage.getItem(key));
            this.applicationByProcessId[processId] = cached;
          }
        }
      }
    },
*/
    hasApplication(processId) {
      return !!this.applicationByProcessId[String(processId)];
    },

    getEnrollButtonLabel(processId) {
      return this.isAuthenticated && this.hasApplication(processId)
        ? 'Editar inscrição'
        : 'Inscrever-se';
    },

    async fetchProcesses() {
      try {
        const response = await this.$axios.get(`${this.$APIPATH}/processes`, { skipAuth: true });
        this.items = Array.isArray(response.data?.data) ? response.data.data : [];
        await this.fetchActiveDetails();
        this.$store.dispatch('updateActiveProcesses', this.activeDetailsById);
        this.$nextTick(() => {
          if (!this.selectedYear) this.selectedYear = this.availableYears[0] || null;
        });
      } catch (error) {
        this.drupalUnavailable = true;
      }
    },

    async fetchActiveDetails() {
      const actives = (this.items || []).filter(
        (p) => p.status === 'started' || p.status === 'in_progress'
      );

      const toFetch = actives.filter((p) => !this.activeDetailsById[String(p.id)]);

      await Promise.all(
        toFetch.map(async (p) => {
          try {
            const res = await this.$axios.get(`${this.$APIPATH}/processes/${p.id}`, { skipAuth: true });
            const proc = res?.data?.data?.[0];

            if (proc) {
              this.activeDetailsById[String(p.id)] = proc;
            }
          } catch (e) {
            console.warn('Falha ao carregar detalhe do ativo:', p?.id, e);
          }
        })
      );
    },

    hasDetail(processId) {
      return !!this.activeDetailsById[String(processId)];
    },

    parseDate(value) {
      if (!value) return null;
      const dt = new Date(value);
      if (Number.isNaN(dt.getTime())) return null;
      return dt;
    },

    isWithin(now, start, end) {
      const s = this.parseDate(start);
      const e = this.parseDate(end);
      if (!s || !e) return false;
      return now.getTime() >= s.getTime() && now.getTime() <= e.getTime();
    },

    getPublicPhases(processId) {
      const proc = this.activeDetailsById[String(processId)];
      const phases = Array.isArray(proc?.phases) ? proc.phases : [];

      return phases
        .filter((p) => p?.restricted !== true)
        .slice()
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    },

    getSortedPublicPhases(processId) {
      return this.getPublicPhases(processId);
    },

    getCurrentPhase(processId) {
      const phases = this.getSortedPublicPhases(processId);
      if (!phases.length) return null;

      const now = new Date();
      return phases.find((ph) => this.isWithin(now, ph?.start, ph?.end)) || null;
    },

    getPhaseLabel(phase) {
      if (!phase) return '—';
      if (phase.name) return String(phase.name);

      const map = {
        registration: 'Inscrição',
        documentation: 'Documentação',
        analysis: 'Análise',
        evaluation: 'Avaliação',
        results: 'Resultados',
      };
      return map[phase.type] || String(phase.type || '—');
    },
    userHasApplication(processId) {
      for (let i=0; i<this.userApplications.length;i++) {
        if (this.userApplications[i].process_id === processId) {
          return true;
        }
      }
      return false;
    },
    shouldShowEnroll(processId) {
      if (this.shouldShowApprovedInActive(processId)) {
        return false;
      }

      const current = this.getCurrentPhase(processId);
      if (current?.type === 'registration') {
        return this.registrationState(processId) === 'open';
      }
      return current == null && this.registrationState(processId) === 'open';
    },

    enrollStatusLabel(processId) {
      if (this.shouldShowApprovedInActive(processId)) {
        return 'Lista de aprovações';
      }

      const st = this.registrationState(processId);
      if (st === 'closed') return 'Inscrições encerradas';
      if (st === 'not_started') return 'Inscrições não iniciadas';
      return 'Inscrição indisponível';
    },

    registrationState(processId) {
      const ph = this.getRegistrationPhase(processId);
      if (!ph) return 'unknown';

      const now = new Date();
      const s = this.parseDate(ph.start);
      const e = this.parseDate(ph.end);

      if (!s || !e) return 'unknown';
      if (now.getTime() < s.getTime()) return 'not_started';
      if (now.getTime() > e.getTime()) return 'closed';
      return 'open';
    },

    getRegistrationPhase(processId) {
      const phases = this.getPublicPhases(processId);
      return phases.find((ph) => ph?.type === 'registration') || null;
    },

    isPhaseFinished(phase) {
      if (!phase?.end) return false;

      const now = new Date();
      const end = this.parseDate(phase.end);

      if (!end) return false;

      return now.getTime() > end.getTime();
    },

    getFirstPhaseAfterRegistration(processId) {
      const phases = this.getPublicPhases(processId);
      if (!phases.length) return null;

      const registrationIndex = phases.findIndex((ph) => ph?.type === 'registration');
      if (registrationIndex < 0) return null;

      return phases[registrationIndex + 1] || null;
    },

    getApprovedPhaseId(processId) {
      const phase = this.getFirstPhaseAfterRegistration(processId);
      return phase?.id ? String(phase.id) : null;
    },

    shouldShowApprovedInActive(processId) {
      if (!this.hasDetail(processId)) return false;

      const registrationPhase = this.getRegistrationPhase(processId);
      const firstPhaseAfterRegistration = this.getFirstPhaseAfterRegistration(processId);

      if (!registrationPhase || !firstPhaseAfterRegistration) return false;

      const registrationClosed = this.registrationState(processId) === 'closed';
      const firstPhaseFinished = this.isPhaseFinished(firstPhaseAfterRegistration);

      return registrationClosed && firstPhaseFinished;
    },

    formatDateTime(value) {
      if (!value) return '—';
      const dt = new Date(value);
      if (Number.isNaN(dt.getTime())) return String(value);
      const date = dt.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      const time = dt.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      });
      return `${date} (${time})`;
    },

    async goToApproved(processId) {
      try {
        const res = await this.$axios.get(`${this.$APIPATH}/processes/${processId}`, { skipAuth: true });
        const proc = res?.data?.data?.[0];

        const rawPhases = Array.isArray(proc?.phases) ? proc.phases : [];
        const phases = rawPhases
          .filter((p) => p?.restricted !== true)
          .slice()
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

        if (!phases.length) {
          console.warn("Processo sem fases utilizáveis:", processId);
          return;
        }

        const process = this.processedItems.find(
          (item) => String(item.id) === String(processId)
        );

        let targetPhase = null;

        if (process?.status === "finished") {
          targetPhase = phases[phases.length - 1];
        } else {
          // obtem ultima fase concluida
          let highEnd=0;
          let result = null;
          let now = new Date();
          for (let i=0; i<phases.length; i++) {
            let end = new Date(phases[i].end);
            if (now > end) {
              if (end > highEnd) {
                highEnd = end;
                result = phases[i];
              }
            }
          }
          targetPhase = result ? result : this.getCurrentPhase(processId) || phases[0];
        }

        if (!targetPhase?.id) {
          console.warn("Não foi possível determinar a fase do processo:", processId);
          return;
        }

        this.$router.push({
          name: "approved-candidates",
          params: {
            id: String(processId),
            phaseId: String(targetPhase.id),
          },
        });
      } catch (e) {
        console.error("Erro ao carregar fases do processo:", e);
      }
    },

    handleEnroll(process, options = {}) {
      let type = process.type;

      if (
        type === 'regular' &&
        process.name.toLowerCase().includes('mestrado profissional')
      ) {
        type = 'profissional';
      }

      this.$store.commit('setProcessId', process.id);
      localStorage.setItem('processId', process.id);
      this.$store.commit('setType', type);
      localStorage.setItem('type', type);

      if (!this.isAuthenticated) {
        const url = `${window.location.origin}/form`;
        keycloak.login({ redirectUri: url });
        this.loadUserContext();
        return;
      }

      options.summary? this.$router.push('/form?summary=true') : this.$router.push('/form');
    },

    translateStatus(status) {
      const map = {
        started: 'Em andamento',
        in_progress: 'Em andamento',
        finished: 'Finalizado',
        scheduled: 'Agendado',
      };
      return map[status] || status;
    },

    translateType(type) {
      const map = {
        special: 'Aluno Especial',
        regular: 'Regular',
        profissional: 'Mestrado Profissional',
      };
      return map[type] || type;
    },

    extractYear(name) {
      if (!name) return null;
      const match = String(name).match(/(?:\b|s)(20\d{2})\b/);
      return match ? parseInt(match[1], 10) : null;
    },

    normalizeName(name) {
      if (!name) return '';
      let s = String(name).trim();
      s = s.replace(/([^\s])(?=(?:Ano\b|Semestre\b|1º\b|2º\b))/gi, '$1 ');
      s = s.replace(/ ?[\u2012\u2013\u2014-]+ ?/g, ' – ');
      s = s.replace(/\s+/g, ' ').trim();
      return s;
    },

    splitProcess(name) {
      const s = this.normalizeName(name);
      if (s.includes(' – ')) {
        const parts = s.split(' – ').map((p) => p.trim()).filter(Boolean);
        return { base: parts[0] || '', period: parts.slice(1).join(' – ') || '' };
      }

      const periodPattern =
        /(1º\s*Semestre.*20\d{2}|2º\s*Semestre.*20\d{2}|Ano(?:\s*Acadêmico)?\s*20\d{2}|Semestre.*20\d{2})/i;
      const pm = s.match(periodPattern);
      if (pm) {
        const period = pm[0].trim();
        const base = s.replace(pm[0], '').trim();
        return { base: base || s, period };
      }

      const yearMatch = s.match(/\b20\d{2}\b/);
      if (yearMatch) {
        const yi = yearMatch.index;
        const base = s.slice(0, yi).trim();
        const period = s.slice(yi).trim();
        return { base: base || s, period };
      }

      return { base: s, period: '' };
    },

    login() {
      if (keycloak) keycloak.login();
    },

    logout() {
      if (keycloak) {
        keycloak.logout({ redirectUri: window.location.origin + '/' });
        this.$store.commit('setIsAuthenticated', false);
        this.$store.commit('setUserData', { given_name: '', family_name: '', email: '' });
        this.$store.commit('setToken', '');
      }
    },
  },
};
</script>

<style scoped>
.title-row {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.title-text {
  min-width: 0;
}

.process-title {
  display: block;
  white-space: normal !important;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.15;
  font-size: 1.08rem;
}

.process-period {
  display: block;
  white-space: normal !important;
  overflow-wrap: anywhere;
  word-break: break-word;
  font-size: 1rem;
  opacity: 0.95;
  margin-top: 0.25rem;
}

.meta-line {
  font-size: 1.02rem;
}

.phase-line {
  font-size: 1rem;
  line-height: 1.25;
}

.max-w-xs {
  max-width: 200px;
}

.card-actions {
  min-height: 72px;
}

.action-area {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 44px;
}

.status-pill {
  height: 40px;
  font-weight: 600;
  letter-spacing: 0.2px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
}
</style>